import { createContext, useContext, type ReactNode } from "react";

interface PdfCtxState {
  isPdf: boolean;
  isStatic?: boolean;
  removeBadge?: boolean;
  shortUrl?: string;
  orgLogo?: string;
  printMode?: "single-page" | "many-pages";
  isSealPDF?: boolean;
  acceptPayload?: Record<string, string>;
  isPreviewDialog?: boolean;
  isFullScreenPreview?: boolean;
}

const initState: PdfCtxState = {
  isPdf: false,
  isStatic: false,
  removeBadge: false,
};

export const PdfContext = createContext<PdfCtxState>({
  ...initState,
});

interface Props {
  children?: ReactNode;
  data: PdfCtxState;
}

export const PdfContextProvider = ({ data, children }: Props) => (
  <PdfContext.Provider value={{ ...initState, ...data }}>
    {children}
  </PdfContext.Provider>
);
export const usePdfDetails = (): PdfCtxState => {
  const ctx = useContext(PdfContext);
  if (!ctx) return { ...initState };
  return ctx;
};

export const useIsPdf = (): boolean => {
  const { isPdf } = usePdfDetails();
  return isPdf;
};

export const useIsStatic = (): boolean => {
  const { isStatic } = usePdfDetails();
  return !!isStatic;
};

export const getPdfDataOnSetup = (event?: Event) => {
  const url = new URL(window.location.toString());
  const detail = (event as CustomEvent)?.detail || {};

  let isPdf = false;
  const pdfRemoveBadge =
    detail.pdfRemoveBadge ??
    url.searchParams.get("pdfRemoveSdBranding") === "true";

  const storyShortUrl =
    detail.storyShortUrl ?? url.searchParams.get("storyShortUrl");

  const pdfOrgLogo = detail.pdfOrgLogo ?? url.searchParams.get("sdOrgLogo");
  const pdfPrintMode =
    detail.pdfPrintMode ??
    url.searchParams.get("pdfPrintMode") ??
    "single-page";

  const isSealPDF =
    detail.sealPDF ?? url.searchParams.get("sealPDF") === "true";
  const acceptPayload = detail.acceptPayload;

  if (window.location.href.includes("printToPdf=true") || detail.printToPdf) {
    isPdf = true;
  }

  const isStatic = window.location.href.includes("static=true");
  const isPreviewDialog = window.location.href.includes("previewDialog=true");
  const isFullScreenPreview = window.location.href.includes("fullscreen=true");
  return {
    isPdf,
    isStatic,
    removeBadge: pdfRemoveBadge,
    shortUrl: storyShortUrl,
    orgLogo: pdfOrgLogo,
    printMode: pdfPrintMode,
    isSealPDF,
    acceptPayload,
    isPreviewDialog,
    isFullScreenPreview,
  };
};
